/**
 * Branch settings — one full page per restaurant.
 *
 * Everything here used to live in `seedData.ts`, which meant changing a phone
 * number was a code change, a review, a build and a deploy. A manager needs to
 * do it on a Thursday evening.
 *
 * The guest notice is the part with teeth, so it gets its own section and its
 * own explanation on screen: it is the only field here that changes what a
 * guest sees on the public booking page, in red, immediately.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useVenues } from '../hooks/useVenue.js';

interface BranchSettings {
  id: string;
  slug: string;
  booking_ref_prefix: string;
  name_en: string;
  name_ar: string;
  address_en: string | null;
  address_ar: string | null;
  phone: string | null;
  phone_alt: string | null;
  email: string | null;
  whatsapp_display_number: string | null;
  internal_notes: string | null;
  guest_notice_en: string | null;
  guest_notice_ar: string | null;
  guest_notice_until: string | null;
  notice_blocks_booking: boolean;
  deposit_from_party_size: number | null;
  deposit_amount_per_cover: string | null;
  deposit_refundable_until_h: number | null;
  is_active: boolean;
}

type Draft = Partial<Record<keyof BranchSettings, string | boolean | null>>;

export function BranchScreen() {
  const { t } = useTranslation();
  const { venueId, setVenue, can } = useSession();
  const queryClient = useQueryClient();
  const { data: venues } = useVenues();

  const [draft, setDraft] = useState<Draft>({});
  const [status, setStatus] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  /**
   * Adding a branch.
   *
   * Owner-only, and the server checks that independently — a venue manager runs
   * the restaurant they are in, not the company's estate.
   *
   * The new branch is NOT switched to automatically. It has no rooms, no tables
   * and no shifts yet, so landing on its floor plan would show an empty canvas
   * that reads as a bug. The manager is told it exists and chooses when to go.
   */
  const createBranch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ venue: BranchSettings }>('/api/v1/venues', body),
    onSuccess: (res) => {
      setShowAdd(false);
      setStatus(t('branch.created', { name: res.venue.name_en }));
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (err: unknown) =>
      setStatus(err instanceof ApiError ? err.message : t('errors.generic')),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['branch', venueId],
    queryFn: () => api.get<{ venue: BranchSettings }>(`/api/v1/venues/${venueId}/settings`),
  });

  // Clear unsaved edits when the venue switcher moves to another branch —
  // otherwise a half-typed address follows you across restaurants.
  useEffect(() => {
    setDraft({});
    setStatus(null);
  }, [venueId]);

  const save = useMutation({
    mutationFn: (body: Draft) =>
      api.patch<{ venue: BranchSettings }>(`/api/v1/venues/${venueId}/settings`, body),
    onSuccess: () => {
      setDraft({});
      setStatus(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['branch', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (err: unknown) =>
      setStatus(err instanceof ApiError ? err.message : t('errors.generic')),
  });

  if (isLoading || !data) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  const venue = data.venue;
  const value = <K extends keyof BranchSettings>(key: K): string =>
    String(draft[key] ?? venue[key] ?? '');
  const set = (key: keyof BranchSettings, v: string | boolean | null) =>
    setDraft((current) => ({ ...current, [key]: v }));

  const dirty = Object.keys(draft).length > 0;

  const submit = () => {
    // Empty strings mean "clear this", except for the fields the API requires.
    const NUMERIC = new Set([
      'deposit_from_party_size',
      'deposit_amount_per_cover',
      'deposit_refundable_until_h',
    ]);

    const body: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(draft)) {
      const required = key === 'name_en' || key === 'name_ar' || key === 'booking_ref_prefix';
      const blank = typeof v === 'string' && v.trim() === '';
      if (blank) {
        body[key] = required ? v : null;
      } else if (NUMERIC.has(key) && v !== null) {
        // The inputs give strings; the schema wants numbers, and a string here
        // fails validation with a message about types that means nothing to a
        // manager who typed "8".
        body[key] = Number(v);
      } else {
        body[key] = v;
      }
    }
    save.mutate(body as Draft);
  };

  return (
    <div className="flex max-w-3xl flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{t('branch.title')}</h1>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">
          {venue.slug}
        </span>
        {status ? <span className="text-sm text-slate-600">{status}</span> : null}
        {can('user:manage') ? (
          <button
            type="button"
            onClick={() => {
              setShowAdd((v) => !v);
              setStatus(null);
            }}
            className="ms-auto rounded-md border border-brand-teal px-3 py-1.5 text-sm font-medium text-brand-teal hover:bg-brand-teal-light"
          >
            {t('branch.addBranch')}
          </button>
        ) : null}
      </div>

      {/* Which branch this page is editing, and how to move between them. The
          header switcher does the same thing, but someone who came here to add
          a branch is thinking about the list, not about the toolbar. */}
      {(venues ?? []).length > 1 ? (
        <div className="flex flex-wrap items-center gap-2">
          {(venues ?? []).map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVenue(v.id)}
              aria-pressed={v.id === venueId}
              className={[
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                v.id === venueId
                  ? 'bg-brand-teal text-white'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-50',
              ].join(' ')}
            >
              {i18nName(v)}
            </button>
          ))}
        </div>
      ) : null}

      {showAdd ? (
        <AddBranchForm
          pending={createBranch.isPending}
          onCancel={() => setShowAdd(false)}
          onSubmit={(body) => createBranch.mutate(body)}
        />
      ) : null}

      <Section title={t('branch.identity')}>
        <Field label={t('branch.nameEn')}>
          <input
            value={value('name_en')}
            onChange={(e) => set('name_en', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.nameAr')}>
          <input
            dir="rtl"
            value={value('name_ar')}
            onChange={(e) => set('name_ar', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.refPrefix')} hint={t('branch.refPrefixHint', { prefix: venue.booking_ref_prefix })}>
          <input
            value={value('booking_ref_prefix')}
            onChange={(e) => set('booking_ref_prefix', e.target.value.toUpperCase())}
            maxLength={4}
            className={`${inputClass} font-mono uppercase`}
          />
        </Field>
      </Section>

      <Section title={t('branch.contact')}>
        <Field label={t('branch.addressEn')}>
          <input
            value={value('address_en')}
            onChange={(e) => set('address_en', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.addressAr')}>
          <input
            dir="rtl"
            value={value('address_ar')}
            onChange={(e) => set('address_ar', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.phone')}>
          <input
            dir="ltr"
            value={value('phone')}
            onChange={(e) => set('phone', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.phoneAlt')}>
          <input
            dir="ltr"
            value={value('phone_alt')}
            onChange={(e) => set('phone_alt', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.email')}>
          <input
            dir="ltr"
            type="email"
            value={value('email')}
            onChange={(e) => set('email', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.whatsapp')} hint={t('branch.whatsappHint')}>
          <input
            dir="ltr"
            inputMode="numeric"
            value={value('whatsapp_display_number')}
            onChange={(e) => set('whatsapp_display_number', e.target.value.replace(/\D/g, ''))}
            className={`${inputClass} font-mono`}
          />
        </Field>
      </Section>

      {/* The only section on this page a guest ever sees. */}
      <section className="rounded-lg border-2 border-red-200 bg-red-50/40 p-4">
        <h2 className="font-semibold text-red-800">{t('branch.notice')}</h2>
        <p className="mt-1 text-sm text-slate-700">{t('branch.noticeHelp')}</p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label={t('branch.noticeEn')}>
            <textarea
              rows={2}
              value={value('guest_notice_en')}
              onChange={(e) => set('guest_notice_en', e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label={t('branch.noticeAr')}>
            <textarea
              dir="rtl"
              rows={2}
              value={value('guest_notice_ar')}
              onChange={(e) => set('guest_notice_ar', e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label={t('branch.noticeUntil')} hint={t('branch.noticeUntilHint')}>
            <input
              type="datetime-local"
              value={toLocalInput(String(draft.guest_notice_until ?? venue.guest_notice_until ?? ''))}
              onChange={(e) =>
                set('guest_notice_until', e.target.value ? new Date(e.target.value).toISOString() : null)
              }
              className={inputClass}
            />
          </Field>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(draft.notice_blocks_booking ?? venue.notice_blocks_booking)}
              onChange={(e) => set('notice_blocks_booking', e.target.checked)}
              className="mt-1 accent-red-600"
            />
            <span>
              <span className="font-medium text-red-800">{t('branch.noticeBlocks')}</span>
              <span className="mt-0.5 block text-slate-600">{t('branch.noticeBlocksHint')}</span>
            </span>
          </label>
        </div>
      </section>

      <Section title={t('branch.deposit')}>
        <Field label={t('branch.depositFrom')} hint={t('branch.depositFromHint')}>
          <input
            type="number"
            min={1}
            max={50}
            value={value('deposit_from_party_size')}
            onChange={(e) =>
              set('deposit_from_party_size', e.target.value === '' ? null : e.target.value)
            }
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.depositAmount')} hint={t('branch.depositAmountHint')}>
          <input
            type="number"
            min={0}
            step="0.01"
            value={value('deposit_amount_per_cover')}
            onChange={(e) =>
              set('deposit_amount_per_cover', e.target.value === '' ? null : e.target.value)
            }
            className={inputClass}
          />
        </Field>
        <Field label={t('branch.depositRefundable')} hint={t('branch.depositRefundableHint')} wide>
          <input
            type="number"
            min={0}
            max={720}
            value={value('deposit_refundable_until_h')}
            onChange={(e) =>
              set('deposit_refundable_until_h', e.target.value === '' ? null : e.target.value)
            }
            className={inputClass}
          />
        </Field>
      </Section>

      <Section title={t('branch.internal')}>
        <Field label={t('branch.internalNotes')} hint={t('branch.internalNotesHint')} wide>
          <textarea
            rows={3}
            value={value('internal_notes')}
            onChange={(e) => set('internal_notes', e.target.value)}
            className={inputClass}
          />
        </Field>
      </Section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={!dirty || save.isPending}
          className="rounded-md bg-brand-teal px-4 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
        >
          {t('branch.save')}
        </button>
        {dirty ? (
          <button
            type="button"
            onClick={() => setDraft({})}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            {t('branch.discard')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

const inputClass = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="font-semibold text-slate-900">{title}</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  wide,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1 text-sm ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-slate-600">{label}</span>
      {children}
      {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

/**
 * ISO instant to the value a `datetime-local` input wants.
 *
 * That input has no timezone, and `toISOString()` is UTC — using it directly
 * would show a Doha manager a time three hours behind the one they set.
 */
function toLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The venue name in the active language. */
function i18nName(v: { name_en: string; name_ar: string }): string {
  return document.documentElement.lang === 'ar' ? v.name_ar : v.name_en;
}

/**
 * Adding a branch.
 *
 * Only the four fields the server insists on. Address, phones and the guest
 * notice are edited on the page this creates — asking for fifteen fields before
 * a restaurant exists is how someone abandons the form halfway and ends up with
 * nothing.
 */
function AddBranchForm({
  pending,
  onCancel,
  onSubmit,
}: {
  pending: boolean;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const [nameEn, setNameEn] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [slug, setSlug] = useState('');
  const [prefix, setPrefix] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  /** "Yasmine Palace Vendome" -> "yasmine-palace-vendome". */
  const slugify = (value: string): string =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60);

  return (
    <form
      className="grid gap-3 rounded-lg border-2 border-brand-teal bg-brand-teal-light/30 p-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name_en: nameEn,
          name_ar: nameAr,
          slug: slug || slugify(nameEn),
          booking_ref_prefix: prefix.toUpperCase(),
        });
      }}
    >
      <Field label={t('branch.nameEn')}>
        <input
          required
          value={nameEn}
          onChange={(e) => {
            setNameEn(e.target.value);
            // Follows the name until someone edits it by hand, then stops:
            // a slug that keeps rewriting itself under a typing finger is
            // worse than one you have to fill in.
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          className={inputClass}
        />
      </Field>
      <Field label={t('branch.nameAr')}>
        <input required dir="rtl" value={nameAr} onChange={(e) => setNameAr(e.target.value)} className={inputClass} />
      </Field>
      <Field label={t('branch.slug')} hint={t('branch.slugHint', { slug: slug || 'yasmine-vendome' })}>
        <input
          required
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(slugify(e.target.value));
          }}
          className={`${inputClass} font-mono`}
        />
      </Field>
      <Field label={t('branch.refPrefix')} hint={t('branch.refPrefixNewHint')}>
        <input
          required
          value={prefix}
          onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
          minLength={2}
          maxLength={4}
          className={`${inputClass} font-mono uppercase`}
        />
      </Field>

      <div className="flex items-end gap-2 sm:col-span-2">
        <button
          type="submit"
          disabled={pending}
          className="rounded-md bg-brand-teal px-3 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
        >
          {t('branch.createBranch')}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
          {t('branch.discard')}
        </button>
        <span className="text-xs text-slate-600">{t('branch.thenAddRooms')}</span>
      </div>
    </form>
  );
}
